import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { routes } from '@/lib/routes';
import { researcherApi } from '@/features/workspace/api';
import { ModuleGrid, WorkspaceHero } from '@/features/workspace/components/WorkspaceShell';
import { ROLE_MODULE_THEMES } from '@/features/workspace/roleModuleThemes';
import { WorkspaceModulePage } from '@/features/workspace/WorkspaceModulePage';
import type { WorkspaceSummary } from '@/features/workspace/types';
import { ResearchToolsPage } from '@/features/research-tools';
import { FileHubPage } from '@/features/file-hub';
import {
  CabinetWorkspaceFrame,
  ScientificGpsBlock,
} from '@/features/cabinet/CabinetWorkspaceFrame';

const ResearcherHome = ({ summary }: { summary: WorkspaceSummary }) => (
  <CabinetWorkspaceFrame
    links={{
      notebook: `${routes.researcher}/files`,
      calendar: routes.newRequest,
      meetings: routes.portalSection('education'),
      connections: routes.portalSection('contacts'),
      alliances: routes.portalSection('contacts'),
      qa: routes.portalSection('instructions'),
      support: routes.portalSection('contacts'),
    }}
  >
    <WorkspaceHero
      profile={summary.profile}
      kpis={summary.kpis}
      createRequestTo={routes.newRequest}
      assistantTo={routes.assistant}
      accent="#41d2ff"
    />
    <section className="role-cabinet">
      <ModuleGrid
        modules={summary.modules}
        basePath={routes.researcher}
        theme={ROLE_MODULE_THEMES.researcher}
      />
    </section>
    <ScientificGpsBlock openTo={`${routes.researcher}/tools`} />
  </CabinetWorkspaceFrame>
);

const ResearcherPage = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [summary, setSummary] = useState<WorkspaceSummary | null>(null);
  const [error, setError] = useState(false);
  const backToCabinet = () => navigate(routes.researcher);
  const canAccess = hasRole('RESEARCHER') || hasRole('SYSTEM_OWNER');

  useEffect(() => {
    researcherApi
      .workspace()
      .then(setSummary)
      .catch(() => setError(true));
  }, []);

  if (!canAccess) {
    return <Navigate to={routes.denied} replace />;
  }

  if (error) {
    return <Navigate to={routes.denied} replace />;
  }

  if (!summary) {
    return <section className="dashboard workspace-page" />;
  }

  return (
    <Routes>
      <Route index element={<ResearcherHome summary={summary} />} />
      <Route
        path="tools/*"
        element={<ResearchToolsPage onClose={backToCabinet} onBack={backToCabinet} />}
      />
      <Route
        path="files/*"
        element={<FileHubPage onClose={backToCabinet} onBack={backToCabinet} />}
      />
      <Route
        path=":moduleKey"
        element={
          <WorkspaceModulePage
            modules={summary.modules}
            basePath={routes.researcher}
            loadModule={(key, params) =>
              researcherApi.module(key, {
                tab: params.tab,
                filter: params.filter,
                search: params.search || undefined,
              })
            }
          />
        }
      />
    </Routes>
  );
};

export default ResearcherPage;
